import { useQuery } from '@tanstack/react-query';
import {
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { UNSAFE_LocationContext } from 'react-router';

import { AppointmentUpdatesPrompt } from '@/features/storefront/pwa/OrderUpdatesPrompt';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { CheckoutPausedNote } from '@/features/storefront/components/CheckoutPause';
import { useCheckoutPause } from '@/features/storefront/hooks/useCheckoutPause';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import {
  NO_TEAM,
  type StorefrontTeam,
  type StorefrontTeamMember,
} from '@/features/storefront/api/team';
import {
  FIELD_STYLE,
  calendarLocale,
  dayPeriod,
  firstOpenDay,
  firstOpenPage,
  formatDay,
  formatTime,
  stripDays,
  weekOpens,
  type DayPeriod,
} from '@/features/storefront/sections/bookingDays';
import type { TokenSession } from '@/features/storefront/api/storefront';
import {
  bookingPhoneOk,
  DayStrip,
  QuestionField,
  TokenSessionCards,
  WhatsAppOptIn,
} from '@/features/storefront/sections/bookingParts';
import { useBookingWhatsApp } from '@/features/storefront/hooks/useStorefrontWhatsApp';
import {
  BookingPaymentOptions,
  BookingPaymentOutcome,
  paymentSummary,
} from '@/features/storefront/sections/bookingPayment';
import { useBookingPayment } from '@/features/storefront/sections/useBookingPayment';
import {
  isoDay,
  mondayFirst,
  openSlots,
  sessionKey,
  staffSlots,
} from '@/features/storefront/sections/bookingSlots';
import {
  activeBookingSteps,
  answersPayload,
  bookingSteps,
  firstUnanswered,
  shownQuestions,
  type BookingAnswers,
} from '@/features/storefront/sections/bookingSteps';
import type {
  BookingHours,
  BookingService,
  BookingStep,
  BookingStepKind,
  BookingsSection,
} from '@/features/storefront/sections/schema';
import {
  bookableStaff,
  combinedHours,
  eligibleStaff,
  formatAvailableDate,
  initials,
  memberPhoto,
  memberFromSearch,
  memberHours,
  offersService,
  serviceFromSearch,
  teamAvailability,
} from '@/features/storefront/sections/team';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { focalStyle } from '@/features/storefront/focal';
import { cn } from '@/lib/cn';
import { fixedImageSizes, gridImageSizes } from '@/lib/imageRenditions';
import { formatMoney } from '@/lib/money';

/**
 * The bookings section as a step-by-step form (`form_layout: "steps"`).
 *
 * The same booking as `BookingCalendar` - the same diary asked the same
 * questions, the same request sent - but one question at a time: what, with
 * whom, when, who you are, and a last look before it is sent. Which of those
 * the shopper meets, in what order and under what names is the merchant's
 * (`section.steps`, made safe by `bookingSteps`); which have nothing to ask on
 * this shop right now (one service, nobody to choose) is decided here, by
 * `activeBookingSteps`.
 *
 * Beside the step, a live summary of what has been chosen so far
 * (`show_summary`): a sticky column from `lg` up, a card under the step, or -
 * on a phone, and in the narrow booking card - a bar above the Next button
 * that opens to the same list. Over it, a progress indicator (`show_progress`):
 * numbered steps with their names on a wide screen, a thin bar and "Step 2 of
 * 4" on a phone.
 *
 * **What it owns that the one-page form does not:** the choice of service.
 * The one-page form is handed a service by the section around it; here the
 * service is a step like any other, so the wizard is handed the list instead
 * and the caller draws nothing else. Every caller - the section, the booking
 * card beside a profile or a service, a profile's Book tab - switches on
 * `form_layout` and draws one or the other.
 *
 * Nothing is sent until the last step. Next stays disabled until the step is
 * answered, Enter in a field is Next, focus moves to each new step's heading
 * so a screen reader hears where it is, and the one bit of motion (a step
 * fading in) is off for a shopper who asked for less.
 */
export interface BookingWizardProps {
  section: BookingsSection;
  /** What can be booked here, in the merchant's order. One is no question. */
  services: readonly BookingService[];
  /** The service to start on - a service's own page. Unset, the shopper picks. */
  serviceId?: string | null;
  /** True in the customiser: nothing is fetched and nothing is booked. */
  preview: boolean;
  /** The shop's visible people, when the page already has them. */
  team?: StorefrontTeam | null;
  /**
   * The slug of the person this form books - a profile's own calendar. It
   * settles the person, so the person step is not shown at all.
   */
  member?: string | null;
  /** The narrow booking card: no side column, the summary is the bar. */
  compact?: boolean;
  /**
   * Wraps each service card - the section passes its `BlockFrame`, so the
   * customiser can outline a service the way it outlines any block.
   */
  frameService?: (service: BookingService, card: ReactNode) => ReactNode;
}

const MUTED: CSSProperties = { color: 'var(--storefront-muted)' };
const PERIODS: DayPeriod[] = ['morning', 'afternoon', 'evening'];
/** How long a picked card shows its tick before the form moves on by itself. */
const AUTO_ADVANCE_MS = 300;

/**
 * A line that shows on any background: the shop's text colour, faintly. The
 * theme's surface colour is a fill, and on a white page it can all but vanish
 * as a border - a field's edge has to be seen to be typed into.
 */
const LINE = 'color-mix(in srgb, var(--storefront-text) 14%, var(--storefront-background))';

/** The card each step sits in, and the summary beside it. */
const CARD_STYLE: CSSProperties = {
  borderColor: LINE,
  borderRadius: 'calc(var(--storefront-radius) + 4px)',
  backgroundColor: 'var(--storefront-background)',
  boxShadow: '0 1px 2px rgb(15 23 42 / 0.04), 0 12px 32px -16px rgb(15 23 42 / 0.16)',
};

/**
 * The step's entrance, written once and only for readers who have not asked
 * for less motion - the same media query the marquee honours.
 */
const MOTION_CSS = `
@keyframes booking-step-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: no-preference) {
  [data-booking-step-body] { animation: booking-step-in 220ms ease-out; }
}
[data-booking-fields] :is(input:not([type=checkbox]):not([type=radio]), textarea, select) {
  border-color: ${LINE} !important;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}
[data-booking-fields] :is(input:not([type=checkbox]):not([type=radio]), textarea, select):hover {
  border-color: color-mix(in srgb, var(--storefront-text) 32%, var(--storefront-background)) !important;
}
[data-booking-fields] :is(input:not([type=checkbox]):not([type=radio]), textarea, select):focus {
  outline: none;
  border-color: var(--storefront-primary) !important;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--storefront-primary) 16%, transparent);
}`;

/** A chosen card, button or day: the shop's primary, faintly washed behind it. */
function selectedStyle(selected: boolean): CSSProperties {
  return {
    borderColor: selected ? 'var(--storefront-primary)' : LINE,
    borderRadius: 'var(--storefront-radius)',
    backgroundColor: selected
      ? 'color-mix(in srgb, var(--storefront-primary) 8%, var(--storefront-background))'
      : 'var(--storefront-background)',
    boxShadow: selected ? '0 0 0 1px var(--storefront-primary)' : undefined,
  };
}

/** Filled with the primary, for a picked day or time. */
function filledStyle(selected: boolean): CSSProperties {
  return {
    borderColor: selected ? 'var(--storefront-primary)' : LINE,
    borderRadius: 'var(--storefront-radius)',
    backgroundColor: selected ? 'var(--storefront-primary)' : 'transparent',
    color: selected ? 'var(--storefront-background)' : 'var(--storefront-text)',
  };
}

/** The round mark at the end of a card that says "this one" without colour alone. */
function Tick({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden
      className="grid size-5 shrink-0 place-items-center rounded-full border"
      style={{
        borderColor: on ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
        backgroundColor: on ? 'var(--storefront-primary)' : 'transparent',
      }}
    >
      {on ? (
        <svg viewBox="0 0 16 16" className="size-3" fill="none" stroke="var(--storefront-background)" strokeWidth="2.5">
          <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : null}
    </span>
  );
}

/** A form field with its name above it, which every step-by-step field has. */
function LabelledField({
  label,
  multiline = false,
  required = false,
  className,
  ...props
}: {
  label: string;
  multiline?: boolean;
  className?: string;
} & React.InputHTMLAttributes<HTMLInputElement> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <div className={className}>
      {/* The star sits outside the label, so the field's name is "Email"
          rather than "Email *" - `required` already says the rest. */}
      <div className="mb-2 flex gap-1 text-sm font-semibold">
        <label htmlFor={id}>{label}</label>
        {required ? (
          <span aria-hidden style={{ color: 'var(--storefront-primary)' }}>
            *
          </span>
        ) : null}
      </div>
      {multiline ? (
        <textarea
          {...props}
          id={id}
          required={required}
          rows={3}
          className="w-full border px-4 py-3 text-sm"
          style={FIELD_STYLE}
        />
      ) : (
        <input
          {...props}
          id={id}
          required={required}
          className="h-12 w-full border px-4 text-sm"
          style={FIELD_STYLE}
        />
      )}
    </div>
  );
}

/**
 * `date_picker: "calendar"` in the step-by-step form: a month at a time,
 * Monday first, with every day outside the diary's window or on a day the
 * shop (or the person) does not work greyed out.
 */
function MonthCalendar({
  section,
  week,
  day,
  locale,
  onChoose,
}: {
  section: BookingsSection;
  week: BookingHours[];
  day: string;
  locale: string;
  onChoose: (iso: string) => void;
}) {
  const copy = useShopContent();
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const last = new Date(first.getFullYear(), first.getMonth(), first.getDate() + section.booking_window_days);
  const months = (date: Date) => date.getFullYear() * 12 + date.getMonth();
  const startOn = day ? new Date(`${day}T00:00:00`) : first;
  const [shown, setShown] = useState(() => months(startOn));
  const year = Math.floor(shown / 12);
  const month = shown % 12;
  const opening = new Date(year, month, 1);
  const length = new Date(year, month + 1, 0).getDate();
  const lead = mondayFirst(opening);
  const fmt = (options: Intl.DateTimeFormatOptions, date: Date) =>
    new Intl.DateTimeFormat(calendarLocale(locale), options).format(date);
  // Seven weekday initials from a known Monday, in the shop's language.
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    fmt({ weekday: 'short' }, new Date(2024, 0, 1 + index)),
  );
  const arrow = 'grid size-9 place-items-center border text-base disabled:opacity-30';

  return (
    <fieldset data-booking-calendar>
      <legend className="sr-only">{copy('booking.choose_day')}</legend>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label={copy('booking.earlier')}
          disabled={shown <= months(first)}
          onClick={() => setShown(shown - 1)}
          className={arrow}
          style={{ borderColor: 'var(--storefront-surface)', borderRadius: '999px' }}
        >
          <span aria-hidden>‹</span>
        </button>
        <p className="text-sm font-semibold" aria-live="polite">
          {fmt({ month: 'long', year: 'numeric' }, opening)}
        </p>
        <button
          type="button"
          aria-label={copy('booking.later')}
          disabled={shown >= months(last)}
          onClick={() => setShown(shown + 1)}
          className={arrow}
          style={{ borderColor: 'var(--storefront-surface)', borderRadius: '999px' }}
        >
          <span aria-hidden>›</span>
        </button>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center" aria-hidden>
        {weekdays.map((name) => (
          <span key={name} className="py-1 text-[11px] font-semibold uppercase tracking-wide" style={MUTED}>
            {name}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: lead }, (_, index) => (
          <span key={`lead-${index}`} aria-hidden />
        ))}
        {Array.from({ length }, (_, index) => {
          const date = new Date(year, month, index + 1);
          const iso = isoDay(date);
          const inWindow = date >= first && date <= last;
          const open = inWindow && weekOpens(week, date);
          const pressed = iso === day;
          const isToday = iso === isoDay(first);
          return (
            <button
              key={iso}
              type="button"
              disabled={!open}
              aria-pressed={pressed}
              aria-label={`${fmt({ weekday: 'long', day: 'numeric', month: 'long' }, date)}${
                inWindow && !open ? ` · ${copy('booking.no_times')}` : ''
              }`}
              onClick={() => onChoose(iso)}
              className={cn(
                'relative grid aspect-square min-h-10 place-items-center border text-sm disabled:cursor-not-allowed disabled:opacity-35',
                pressed && 'font-semibold',
              )}
              style={{ ...filledStyle(pressed), borderColor: pressed ? 'var(--storefront-primary)' : 'transparent' }}
              data-calendar-day={iso}
            >
              {index + 1}
              {isToday && !pressed ? (
                <span
                  aria-hidden
                  className="absolute bottom-1 size-1 rounded-full"
                  style={{ backgroundColor: 'var(--storefront-primary)' }}
                />
              ) : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * `date_picker: "weeks"`: two rows of a calendar - a fortnight, Monday first -
 * paged two weeks at a time. Short enough to sit beside the times, so the day
 * and the time are one screen rather than a month to scroll past first.
 */
function WeeksCalendar({
  section,
  week,
  day,
  locale,
  onChoose,
}: {
  section: BookingsSection;
  week: BookingHours[];
  day: string;
  locale: string;
  onChoose: (iso: string) => void;
}) {
  const copy = useShopContent();
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const last = new Date(first.getFullYear(), first.getMonth(), first.getDate() + section.booking_window_days);
  // Every page starts on a Monday: the one on or before today, then a fortnight on.
  const origin = new Date(first.getFullYear(), first.getMonth(), first.getDate() - mondayFirst(first));
  const DAY_MS = 86_400_000;
  const pageOf = (date: Date) => Math.max(0, Math.floor(Math.round((date.getTime() - origin.getTime()) / DAY_MS) / 14));
  const lastPage = pageOf(last);
  const [page, setPage] = useState(() => Math.min(pageOf(day ? new Date(`${day}T00:00:00`) : first), lastPage));
  const dates = Array.from(
    { length: 14 },
    (_, index) => new Date(origin.getFullYear(), origin.getMonth(), origin.getDate() + page * 14 + index),
  );
  const fmt = (options: Intl.DateTimeFormatOptions, date: Date) =>
    new Intl.DateTimeFormat(calendarLocale(locale), options).format(date);
  const weekdays = Array.from({ length: 7 }, (_, index) => fmt({ weekday: 'short' }, new Date(2024, 0, 1 + index)));
  const start = dates[0];
  const end = dates[13];
  const title =
    start.getMonth() === end.getMonth()
      ? fmt({ month: 'long', year: 'numeric' }, start)
      : start.getFullYear() === end.getFullYear()
        ? `${fmt({ month: 'short' }, start)} – ${fmt({ month: 'short', year: 'numeric' }, end)}`
        : `${fmt({ month: 'short', year: 'numeric' }, start)} – ${fmt({ month: 'short', year: 'numeric' }, end)}`;
  const arrow = 'grid size-9 place-items-center border text-base disabled:opacity-30';

  return (
    <fieldset data-booking-calendar="weeks">
      <legend className="sr-only">{copy('booking.choose_day')}</legend>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label={copy('booking.earlier')}
          disabled={page <= 0}
          onClick={() => setPage(page - 1)}
          className={arrow}
          style={{ borderColor: 'var(--storefront-surface)', borderRadius: '999px' }}
        >
          <span aria-hidden>‹</span>
        </button>
        <p className="text-sm font-semibold" aria-live="polite">
          {title}
        </p>
        <button
          type="button"
          aria-label={copy('booking.later')}
          disabled={page >= lastPage}
          onClick={() => setPage(page + 1)}
          className={arrow}
          style={{ borderColor: 'var(--storefront-surface)', borderRadius: '999px' }}
        >
          <span aria-hidden>›</span>
        </button>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1.5 text-center" aria-hidden>
        {weekdays.map((name) => (
          <span key={name} className="py-1 text-[11px] font-semibold uppercase tracking-wide" style={MUTED}>
            {name}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {dates.map((date) => {
          const iso = isoDay(date);
          const inWindow = date >= first && date <= last;
          const open = inWindow && weekOpens(week, date);
          const pressed = iso === day;
          const isToday = iso === isoDay(first);
          // The month's name on its first day, and on the first cell, so a
          // fortnight that crosses a month says where it does.
          const showMonth = date.getDate() === 1 || date === dates[0];
          return (
            <button
              key={iso}
              type="button"
              disabled={!open}
              aria-pressed={pressed}
              aria-label={`${fmt({ weekday: 'long', day: 'numeric', month: 'long' }, date)}${
                inWindow && !open ? ` · ${copy('booking.no_times')}` : ''
              }`}
              onClick={() => onChoose(iso)}
              className={cn(
                'relative flex h-14 flex-col items-center justify-center border text-sm disabled:cursor-not-allowed disabled:opacity-35',
                pressed && 'font-semibold',
              )}
              style={{
                ...filledStyle(pressed),
                borderColor: pressed ? 'var(--storefront-primary)' : open ? LINE : 'transparent',
              }}
              data-calendar-day={iso}
            >
              {showMonth ? (
                <span className="text-[10px] uppercase leading-none tracking-wide opacity-70">
                  {fmt({ month: 'short' }, date)}
                </span>
              ) : null}
              <span className="tabular-nums leading-tight">{date.getDate()}</span>
              {isToday && !pressed ? (
                <span
                  aria-hidden
                  className="absolute bottom-1.5 size-1 rounded-full"
                  style={{ backgroundColor: 'var(--storefront-primary)' }}
                />
              ) : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/** One line of the summary or the review: what, and what was chosen. */
interface SummaryRow {
  key: string;
  label: string;
  value: ReactNode | null;
  /** The step that answers it, for the review's Edit link. */
  step?: BookingStepKind;
}

function SummaryList({ rows }: { rows: SummaryRow[] }) {
  const copy = useShopContent();
  return (
    <dl className="grid gap-3 text-sm">
      {rows.map((row) => (
        <div key={row.key} className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-3" data-summary-row={row.key}>
          <dt className="text-xs font-medium uppercase tracking-wide" style={MUTED}>
            {row.label}
          </dt>
          <dd className="min-w-0 break-words">
            {row.value ?? <span style={MUTED}>{copy('booking.not_chosen')}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function BookingWizard({
  section,
  services,
  serviceId = null,
  preview,
  team: given,
  member: named = null,
  compact = false,
  frameService,
}: BookingWizardProps) {
  const copy = useShopContent();
  // Read-only maintenance refuses a booking; a preview is never sent anyway.
  const pausedNow = useCheckoutPause();
  const paused = preview ? null : pausedNow;
  const { locale } = useStorefrontLanguage();
  const scope = useStorefrontScope();
  const source = useStorefrontData();
  const routed = useContext(UNSAFE_LocationContext) as { location: { search: string } } | null;

  const choose = section.staff_mode === 'choose';
  const byPerson = section.staff_mode !== 'none';

  // --- The people, as the one-page form asks for them (same key, one fetch).
  const own = useQuery({
    queryKey: [...scope, 'team-members'],
    queryFn: async () => (source.team ? await source.team() : NO_TEAM),
    enabled: byPerson && given === undefined,
    retry: false,
  });
  const team: StorefrontTeam | null =
    given !== undefined ? given : own.isError ? NO_TEAM : (own.data ?? null);
  const members = useMemo(() => team?.members ?? [], [team]);

  // --- What the shopper has chosen ----------------------------------------
  // A plan card's `?service=` link starts the form on its service, as a
  // product's own booking card does with `serviceId`.
  const [serviceChoice, setServiceChoice] = useState<string | null>(
    () => serviceId ?? serviceFromSearch(services, routed?.location.search ?? null),
  );
  // `undefined` is "not asked yet" - unlike the one-page form, the step form
  // does not start on "any available": Next waits for the shopper to say.
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  const [pageChoice, setPage] = useState<number | undefined>(undefined);
  const [dayChoice, setDay] = useState<string | undefined>(undefined);
  const [chosen, setChosen] = useState<string | null>(null);
  // A token instead of a time, for a person who sees patients in order of
  // token: the session picked. One or the other of `chosen` and this, never both.
  const [token, setToken] = useState<TokenSession | null>(null);
  const [values, setValues] = useState({ name: '', email: '', phone: '', notes: '', address: '' });
  // "Updates on WhatsApp": offered only where the shop sends them and the
  // section asks for the number they would go to, and only ever sent with one.
  const offersWhatsApp = useBookingWhatsApp(!preview);
  const [whatsappOptIn, setWhatsAppOptIn] = useState(false);
  const optedIn = offersWhatsApp && whatsappOptIn && values.phone.trim() !== '';
  const questions = shownQuestions(section);
  const [answers, setAnswers] = useState<BookingAnswers>({});
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');
  const [booked, setBooked] = useState<{
    start: string;
    zone?: string;
    with: string | null;
    /** The patient's number, when a token was taken; `start` is then an estimate. */
    token: number | null;
    /** For following it with push notifications. */
    id: string;
    accessToken: string | null;
  } | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  // A profile's own calendar settles who; a team card's `?member=` link only
  // fills the person step in, leaving the shopper free to change it.
  const personSettled = choose && Boolean(named);
  const slug = named || memberFromSearch(routed?.location.search ?? null);
  const linked: StorefrontTeamMember | undefined =
    choose && slug ? members.find((entry) => entry.slug === slug) : undefined;
  const person: string | null | undefined =
    section.staff_mode === 'fixed'
      ? section.staff_member_id
      : choose
        ? picked !== undefined
          ? picked
          : (linked?.id ?? undefined)
        : null;
  const personRow = person ? (members.find((entry) => entry.id === person) ?? null) : null;

  // The services on offer: on a choose-mode shop whose people are known, the
  // ones the picked person does - or, before anyone is picked, the ones
  // somebody does - so the shopper is never walked to a service nobody takes.
  const offered = useMemo(
    () =>
      services.filter((service) => {
        if (!choose || team === null || (preview && members.length === 0)) return true;
        if (personRow) return offersService(personRow, service.id);
        return eligibleStaff(members, service).length > 0;
      }),
    [services, choose, team, preview, members, personRow],
  );
  const service =
    offered.find((entry) => entry.id === serviceChoice) ??
    (services.length === 1 && offered.includes(services[0]) ? services[0] : null);

  // Who can be picked: those who do the chosen service, or - when the person
  // is asked first - anyone who does any of them.
  const people = useMemo(() => {
    if (service) return bookableStaff(section, service, members);
    if (!choose) return bookableStaff(section, { id: '' }, members);
    return members.filter((entry) => services.some((offer) => offersService(entry, offer.id)));
  }, [section, service, members, choose, services]);

  const week = useMemo<BookingHours[]>(() => {
    if (!byPerson) return section.hours;
    if (personRow) return memberHours(section, personRow);
    if (people.length > 0) return combinedHours(people.map((entry) => memberHours(section, entry)));
    return section.hours;
  }, [byPerson, section, personRow, people]);

  const page = pageChoice ?? firstOpenPage(section, week);
  const days = useMemo(() => stripDays(section, page, week), [section, page, week]);
  const day = dayChoice ?? firstOpenDay(section, week);

  const waitingForPeople = byPerson && !preview && team === null;
  const nobody = choose && !preview && team !== null && service !== null && people.length === 0;

  const availability = useQuery({
    queryKey: [
      ...scope,
      'booking-availability',
      section.id,
      service?.id ?? '',
      day,
      ...(byPerson ? [person ?? 'any'] : []),
    ],
    queryFn: () =>
      source.bookingAvailability({
        section,
        service: service!,
        day,
        teamMemberId: byPerson ? (person ?? null) : undefined,
      }),
    enabled:
      !preview &&
      service !== null &&
      day.length > 0 &&
      person !== undefined &&
      !waitingForPeople &&
      !nobody,
    // Never from cache - see `BookingCalendar`.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const zone = availability.data?.timezone;
  const slots = !service
    ? []
    : preview
      ? byPerson && people.length > 0
        ? staffSlots(section, service, day, personRow ? [personRow] : people)
        : openSlots(section, service, day)
      : (availability.data?.slots ?? []);
  const bookable = slots.filter((slot) => slot.available);
  // Token sessions: in place of the grid for a person who books by token
  // (`mode` tokens), or beside everyone else's times for "any available".
  // The customiser's sample people all book by time, so it has none.
  const sessions = !service || preview ? [] : (availability.data?.sessions ?? []);
  const tokenMode = !preview && availability.data?.mode === 'tokens';
  const needsAddress = service?.location === 'address';
  // What booking costs now, the shopper's answers to it, and the payment
  // once booked - all nothing on a shop without booking payments.
  const payment = useBookingPayment(service, preview);

  // --- The steps -----------------------------------------------------------
  const all = bookingSteps(section);
  const steps = activeBookingSteps(section, { serviceCount: services.length, personSettled });
  const [current, setCurrent] = useState<BookingStepKind | null>(null);
  const found = steps.findIndex((step) => step.kind === current);
  const index = found >= 0 ? found : 0;
  const step: BookingStep | undefined = steps[index];
  const isLast = index === steps.length - 1;

  const singular = team?.singular?.trim() || null;
  function stepLabel(kind: BookingStepKind): string {
    const custom = all.find((entry) => entry.kind === kind)?.label?.trim();
    if (custom) return custom;
    if (kind === 'person') return singular ?? copy('booking.step_person');
    return copy(`booking.step_${kind}`);
  }

  // A name and a phone number are all a booking needs; an email, if given, has to look like one.
  const emailOk = values.email.trim() === '' || /^\S+@\S+\.\S+$/.test(values.email.trim());
  const phoneOk = bookingPhoneOk(values.phone);
  function answered(kind: BookingStepKind): boolean {
    switch (kind) {
      case 'service':
        return service !== null;
      case 'person':
        return person !== undefined;
      case 'datetime':
        return chosen !== null || token !== null;
      case 'details':
        return (
          values.name.trim() !== '' &&
          phoneOk &&
          emailOk &&
          (!needsAddress || values.address.trim() !== '') &&
          firstUnanswered(questions, answers) === null
        );
      default:
        return true;
    }
  }

  // Focus follows the step, but only once the shopper has moved: on arrival
  // the page is theirs to read from the top, not a heading halfway down.
  const heading = useRef<HTMLHeadingElement>(null);
  const moved = useRef(false);
  // On a new step, and on the confirmation that replaces them - not on an
  // error, which is announced where it is and leaves focus on the button.
  const finished = state === 'done';
  useEffect(() => {
    if (moved.current) heading.current?.focus();
  }, [index, finished]);

  // A pick on a one-choice step (a service, a person) moves on by itself,
  // after a beat to see the tick. Any other move cancels a pending one, so a
  // quick Back is never overridden.
  const pickTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(pickTimer.current), []);

  function advanceAfterPick(from: BookingStepKind) {
    if (!step || step.kind !== from || isLast) return;
    const nextKind = steps[index + 1].kind;
    window.clearTimeout(pickTimer.current);
    pickTimer.current = window.setTimeout(() => go(nextKind), AUTO_ADVANCE_MS);
  }

  function go(kind: BookingStepKind) {
    window.clearTimeout(pickTimer.current);
    moved.current = true;
    setCurrent(kind);
    setSummaryOpen(false);
    if (state === 'error') setState('idle');
  }

  function resetTime() {
    setPage(undefined);
    setDay(undefined);
    setChosen(null);
    setToken(null);
  }

  function chooseSlot(startAt: string) {
    setChosen(startAt);
    setToken(null);
  }

  function chooseSession(session: TokenSession) {
    setToken(session);
    setChosen(null);
  }

  function chooseService(id: string) {
    if (id === service?.id) return;
    setServiceChoice(id);
    // Another service is another length, so another grid.
    resetTime();
  }

  function choosePerson(id: string | null) {
    setPicked(id);
    resetTime();
  }

  function chooseDay(next: string) {
    setDay(next);
    setChosen(null);
    setToken(null);
  }

  async function submit() {
    if (preview) return;
    // A step can be skipped past only by going back and changing an earlier
    // answer; the first one that no longer holds is where the shopper goes.
    const missing = (['service', 'person', 'datetime', 'details'] as const).find(
      (kind) => !answered(kind),
    );
    if (missing || !service || (!chosen && !token)) {
      const target = steps.find((entry) => entry.kind === missing) ?? steps[0];
      const question = firstUnanswered(questions, answers);
      if (target) go(target.kind);
      setError(question ? copy('booking.answer_required', { question: question.label.trim() }) : '');
      setState('error');
      return;
    }

    setState('sending');
    setError('');
    try {
      const confirmation = await source.createBooking({
        section,
        service,
        // A token is the session and whose it is - the server gives the number.
        ...(token
          ? { sessionStartsAt: token.starts_at, teamMemberId: token.team_member_id }
          : { startAt: chosen ?? undefined, ...(byPerson ? { teamMemberId: person ?? null } : {}) }),
        name: values.name.trim(),
        email: values.email.trim() || undefined,
        phone: values.phone.trim(),
        notes: section.ask_notes ? values.notes.trim() || undefined : undefined,
        ...(needsAddress ? { customerAddress: values.address.trim() } : {}),
        answers: answersPayload(questions, answers),
        ...(optedIn ? { whatsappOptIn: true } : {}),
        ...payment.request(),
      });
      setBooked({
        start: confirmation.starts_at,
        zone,
        with: confirmation.team_member_name ?? personRow?.name ?? token?.team_member_name ?? null,
        token: confirmation.token_number ?? null,
        id: confirmation.id,
        accessToken: confirmation.access_token ?? null,
      });
      moved.current = true;
      setState('done');
      // Money due opens the gateway over the confirmation, which then says
      // whether it was paid or the slot is only held.
      void payment.settle(
        confirmation,
        { name: values.name.trim(), email: values.email.trim(), phone: values.phone.trim() },
        `${service.name} · ${whenText(confirmation.starts_at, zone)}`,
      );
    } catch (caught) {
      // The server's words, as in the one-page form: "that time has just
      // been taken" tells the shopper what to do next.
      setError(caught instanceof Error ? caught.message : '');
      setState('error');
      void availability.refetch();
    }
  }

  /** The hold ran out: back to the times, the shopper's details kept. */
  function pickAnother() {
    payment.reset();
    setBooked(null);
    setChosen(null);
    setToken(null);
    setState('idle');
    const datetime = steps.find((entry) => entry.kind === 'datetime');
    if (datetime) go(datetime.kind);
    void availability.refetch();
  }

  function advance() {
    if (!step || state === 'sending') return;
    if (!preview && !answered(step.kind)) return;
    if (isLast) {
      if (paused) return;
      void submit();
      return;
    }
    go(steps[index + 1].kind);
  }

  // --- What has been chosen, in words -------------------------------------
  const where = service
    ? service.location === 'online'
      ? copy('booking.location_online')
      : service.location === 'address'
        ? copy('booking.location_address')
        : copy('booking.location_place')
    : null;
  const note = service?.location_note.trim() ?? '';
  const whenText = (start: string, timeZone?: string) =>
    copy('booking.when', { day: formatDay(start, locale, timeZone), time: formatTime(start, locale, timeZone) });
  // A token's time is only an estimate, and says so: "… at approx. 11:30".
  const estimateText = (start: string, timeZone?: string) =>
    copy('booking.when', {
      day: formatDay(start, locale, timeZone),
      time: copy('booking.approx', { time: formatTime(start, locale, timeZone) }),
    });

  const summaryRows: SummaryRow[] = [
    { key: 'service', label: stepLabel('service'), value: service?.name ?? null, step: 'service' },
    ...(byPerson
      ? [
          {
            key: 'person',
            label: stepLabel('person'),
            value:
              person === undefined
                ? null
                : (personRow?.name ??
                  (person === null ? (token?.team_member_name ?? copy('booking.any_person')) : null)),
            step: 'person' as const,
          },
        ]
      : []),
    {
      key: 'when',
      label: stepLabel('datetime'),
      value: chosen ? whenText(chosen, zone) : token ? estimateText(token.estimated_at, zone) : null,
      step: 'datetime',
    },
    ...(token
      ? [
          {
            key: 'token',
            label: copy('booking.summary_token'),
            value: copy('booking.your_token', { number: token.next_token }),
            step: 'datetime' as const,
          },
        ]
      : []),
    ...(section.show_duration && service
      ? [
          {
            key: 'duration',
            label: copy('booking.summary_duration'),
            value: copy('booking.duration', { minutes: service.duration_minutes }),
          },
        ]
      : []),
    ...(section.show_price && service && service.price !== null
      ? [{ key: 'price', label: copy('booking.summary_price'), value: formatMoney(service.price) }]
      : []),
    ...(service && paymentSummary(payment, copy)
      ? [{ key: 'payment', label: copy('booking.summary_payment'), value: paymentSummary(payment, copy) }]
      : []),
    ...(service
      ? [
          {
            key: 'where',
            label: copy('booking.summary_location'),
            value: (
              <>
                <span className="block">{where}</span>
                {note ? (
                  <span className="mt-0.5 block text-xs" style={MUTED}>
                    {note}
                  </span>
                ) : null}
              </>
            ),
          },
        ]
      : []),
  ];

  // --- Done: the wizard gives way to what was booked -----------------------
  if (state === 'done' && booked && service) {
    return (
      <div
        className={cn('border p-6 text-center sm:p-8', compact ? 'mt-4' : 'mt-8')}
        style={{ borderColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' }}
        data-booking-done
      >
        <span
          aria-hidden
          className="mx-auto grid size-12 place-items-center rounded-full"
          style={{ backgroundColor: 'var(--storefront-primary)' }}
        >
          <svg viewBox="0 0 16 16" className="size-6" fill="none" stroke="var(--storefront-background)" strokeWidth="2">
            <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <h3
          ref={heading}
          tabIndex={-1}
          className="mt-4 text-lg font-semibold outline-none"
          style={STOREFRONT_HEADING_STYLE}
        >
          {service.name}
        </h3>
        {booked.token !== null ? (
          <p
            className="mt-3 text-3xl font-bold tabular-nums"
            style={{ color: 'var(--storefront-primary)' }}
            data-booking-token
          >
            {copy('booking.your_token', { number: booked.token })}
          </p>
        ) : null}
        <p className="mt-1 text-sm" style={MUTED}>
          {booked.token !== null ? estimateText(booked.start, booked.zone) : whenText(booked.start, booked.zone)}
        </p>
        {booked.with ? (
          <p className="mt-1 text-sm" data-booking-with>
            {copy('booking.with_person', { name: booked.with })}
          </p>
        ) : null}
        <p className="mt-1 text-sm" style={MUTED}>
          {where}
          {note ? ` · ${note}` : null}
        </p>
        <BookingPaymentOutcome payment={payment} zone={booked.zone} onPickAnother={pickAnother} />
        {payment.phase.kind === 'expired' ? null : (
          <p className="mx-auto mt-5 max-w-md text-sm">{section.success_message}</p>
        )}
        {/* Reminders on this device, where the shop offers push. */}
        {payment.phase.kind === 'expired' ? null : (
          <AppointmentUpdatesPrompt appointmentId={booked.id} appointmentToken={booked.accessToken} />
        )}
      </div>
    );
  }

  if (!step) return null;

  // --- The steps' contents -------------------------------------------------
  // With a photo on every service (and room for it), each card leads with its
  // picture, four to a row - a gallery to choose from rather than a long list.
  const pictured = !compact && offered.length > 1 && offered.every((entry) => Boolean(entry.image_url));
  const serviceStep = (
    <div
      className={cn(
        'grid gap-3',
        pictured ? 'grid-cols-2 lg:grid-cols-4' : !compact && 'sm:grid-cols-2',
      )}
      role="group"
      aria-label={stepLabel('service')}
      data-service-grid={pictured ? 'pictures' : 'list'}
    >
      {offered.map((entry) => {
        const on = service?.id === entry.id;
        const badge =
          entry.location === 'online'
            ? copy('booking.location_online')
            : entry.location === 'address'
              ? copy('booking.location_address')
              : null;
        const facts = [
          section.show_duration ? copy('booking.duration', { minutes: entry.duration_minutes }) : null,
          section.show_price && entry.price !== null ? formatMoney(entry.price) : null,
        ].filter(Boolean);
        const card = pictured ? (
          <button
            key={entry.id}
            type="button"
            aria-pressed={on}
            onClick={() => {
              chooseService(entry.id);
              advanceAfterPick('service');
            }}
            className="group relative flex h-full w-full flex-col overflow-hidden border text-start transition-colors motion-reduce:transition-none"
            style={selectedStyle(on)}
            data-service-card={entry.id}
          >
            <span className="block aspect-[4/3] w-full overflow-hidden" style={{ backgroundColor: 'var(--storefront-surface)' }}>
              <StorefrontImage
                url={entry.image_url}
                sizes={gridImageSizes(4)}
                alt=""
                className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transition-none"
                style={focalStyle(entry.image_focal)}
              />
            </span>
            <span className="absolute end-2.5 top-2.5 rounded-full" style={{ backgroundColor: 'var(--storefront-background)' }}>
              <Tick on={on} />
            </span>
            <span className="block min-w-0 flex-1 p-3">
              <span className="block text-sm font-semibold leading-snug">{entry.name}</span>
              {facts.length > 0 ? (
                <span className="mt-0.5 block text-xs" style={MUTED}>
                  {facts.join(' · ')}
                </span>
              ) : null}
              {entry.description ? (
                <span className="mt-1 line-clamp-2 block text-xs leading-relaxed" style={MUTED}>
                  {entry.description}
                </span>
              ) : null}
              {badge ? (
                <span
                  className="mt-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                  style={{ borderColor: 'var(--storefront-surface)' }}
                >
                  {badge}
                </span>
              ) : null}
            </span>
          </button>
        ) : (
          <button
            key={entry.id}
            type="button"
            aria-pressed={on}
            onClick={() => {
              chooseService(entry.id);
              advanceAfterPick('service');
            }}
            className="flex h-full w-full items-start gap-3 border p-4 text-start transition-colors motion-reduce:transition-none"
            style={selectedStyle(on)}
            data-service-card={entry.id}
          >
            {entry.image_url ? (
              <StorefrontImage
                url={entry.image_url}
                sizes={fixedImageSizes(56)}
                alt=""
                className="size-14 shrink-0 object-cover"
                style={{ borderRadius: 'var(--storefront-radius)' }}
              />
            ) : null}
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold">{entry.name}</span>
              {facts.length > 0 ? (
                <span className="mt-0.5 block text-xs" style={MUTED}>
                  {facts.join(' · ')}
                </span>
              ) : null}
              {entry.description ? (
                <span className="mt-1.5 line-clamp-2 block text-xs leading-relaxed" style={MUTED}>
                  {entry.description}
                </span>
              ) : null}
              {badge ? (
                <span
                  className="mt-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                  style={{ borderColor: 'var(--storefront-surface)' }}
                >
                  {badge}
                </span>
              ) : null}
            </span>
            <Tick on={on} />
          </button>
        );
        return frameService ? <div key={entry.id}>{frameService(entry, card)}</div> : card;
      })}
      {offered.length === 0 ? (
        <p className="text-sm" style={MUTED} role="status">
          {copy(waitingForPeople ? 'booking.checking' : 'booking.no_people')}
        </p>
      ) : null}
    </div>
  );

  const personCard = (id: string | null) => {
    const entry = id ? people.find((row) => row.id === id) : null;
    const on = person === id;
    const free = entry ? teamAvailability(entry) : null;
    const subtitle = entry ? entry.speciality || entry.role : null;
    const photo = entry ? memberPhoto(entry, members) : null;
    return (
      <button
        key={id ?? '__any'}
        type="button"
        aria-pressed={on}
        onClick={() => {
          choosePerson(id);
          advanceAfterPick('person');
        }}
        className="flex w-full items-center gap-3 border p-3 text-start transition-colors motion-reduce:transition-none"
        style={selectedStyle(on)}
        data-person-card={id ?? 'any'}
      >
        {photo ? (
          <StorefrontImage
            url={photo}
            sizes={fixedImageSizes(48)}
            alt=""
            className="size-12 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span
            aria-hidden
            className="grid size-12 shrink-0 place-items-center rounded-full text-sm font-semibold"
            style={{ backgroundColor: 'var(--storefront-surface)', color: 'var(--storefront-text)' }}
          >
            {entry ? initials(entry.name) : '∗'}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{entry ? entry.name : copy('booking.any_person')}</span>
          {subtitle ? (
            <span className="block text-xs" style={MUTED}>
              {subtitle}
            </span>
          ) : null}
          {free ? (
            <span
              className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium"
              style={{ color: free.kind === 'today' ? 'var(--storefront-primary)' : 'var(--storefront-muted)' }}
            >
              <span aria-hidden className="size-1.5 rounded-full" style={{ backgroundColor: 'currentColor' }} />
              {free.kind === 'today'
                ? copy('team.available_today')
                : copy('team.next_available', { date: formatAvailableDate(free.date, locale) })}
            </span>
          ) : null}
        </span>
        <Tick on={on} />
      </button>
    );
  };

  const personStep = waitingForPeople ? (
    <p className="text-sm" style={MUTED} role="status">
      {copy('booking.checking')}
    </p>
  ) : people.length === 0 ? (
    <p className="text-sm" style={MUTED} role="status">
      {copy('booking.no_people')}
    </p>
  ) : (
    <div className={cn('grid gap-3', !compact && 'sm:grid-cols-2')} role="group" aria-label={stepLabel('person')}>
      {section.allow_any_staff ? personCard(null) : null}
      {people.map((entry) => personCard(entry.id))}
    </div>
  );

  const groups = PERIODS.map((period) => ({
    period,
    slots: bookable.filter((slot) => dayPeriod(slot.start_at, zone) === period),
  })).filter((group) => group.slots.length > 0);

  // The token sessions, as picked from; `showPerson` with "any available".
  const sessionCards = (showPerson: boolean) => (
    <TokenSessionCards
      sessions={sessions}
      chosen={token ? sessionKey(token) : null}
      onChoose={chooseSession}
      locale={locale}
      zone={zone}
      showPerson={showPerson}
      compact={compact}
    />
  );

  // `beside`: the times share the row with a two-week calendar, so fewer columns.
  const slotGroups = (beside: boolean) =>
    groups.map((group) => (
      <div key={group.period} className="mt-4" data-slot-group={group.period}>
        <p className="text-xs font-semibold uppercase tracking-wide" style={MUTED}>
          {copy(`booking.${group.period}`)}
        </p>
        <div
          className={cn(
            'mt-2 grid grid-cols-3 gap-2',
            !compact && (beside ? 'sm:grid-cols-4 md:grid-cols-3 xl:grid-cols-4' : 'sm:grid-cols-4 xl:grid-cols-5'),
          )}
        >
          {group.slots.map((slot) => {
            const on = slot.start_at === chosen;
            return (
              <button
                key={slot.start_at}
                type="button"
                aria-pressed={on}
                onClick={() => chooseSlot(slot.start_at)}
                className={cn('h-11 border px-2 text-sm tabular-nums', on && 'font-semibold')}
                style={filledStyle(on)}
              >
                {formatTime(slot.start_at, locale, zone)}
              </button>
            );
          })}
        </div>
      </div>
    ));

  const timeGrid = (beside = false) =>
    waitingForPeople || (!preview && availability.isPending) ? (
      <p className="mt-3 text-sm" style={MUTED} role="status">
        {copy('booking.checking')}
      </p>
    ) : nobody ? (
      <p className="mt-3 text-sm" style={MUTED} role="status">
        {copy('booking.no_people')}
      </p>
    ) : availability.isError ? (
      <p className="mt-3 text-sm" style={MUTED}>
        {copy('booking.load_error')}
      </p>
    ) : tokenMode ? (
      sessions.length === 0 ? (
        <p className="mt-3 text-sm" style={MUTED}>
          {copy('booking.no_sessions')}
        </p>
      ) : (
        <div data-booking-tokens="only">
          <p className="mt-3 text-sm" style={MUTED}>
            {copy('booking.tokens_hint')}
          </p>
          {sessionCards(false)}
        </div>
      )
    ) : groups.length === 0 && sessions.length === 0 ? (
      <p className="mt-3 text-sm" style={MUTED}>
        {copy('booking.none_free')}
      </p>
    ) : (
      <>
        {slotGroups(beside)}
        {sessions.length > 0 ? (
          <div className="mt-6" data-booking-tokens="beside">
            <p className="text-xs font-semibold uppercase tracking-wide" style={MUTED}>
              {groups.length > 0 ? copy('booking.take_token') : copy('booking.tokens_hint')}
            </p>
            {sessionCards(true)}
          </div>
        ) : null}
      </>
    );

  const datetimeStep = section.date_picker === 'weeks' ? (
    // The fortnight and its times side by side from `md` up - one screen to
    // pick both - and stacked in the narrow card and on a phone.
    <div className={cn('grid gap-6', !compact && 'md:grid-cols-2 md:gap-8')}>
      <WeeksCalendar
        key={`${person ?? 'any'}-${service?.id ?? ''}`}
        section={section}
        week={week}
        day={day}
        locale={locale}
        onChoose={chooseDay}
      />
      <fieldset
        className={cn(!compact && 'md:border-s md:ps-8')}
        style={{ borderColor: 'var(--storefront-surface)' }}
        data-booking-times
      >
        <legend className="text-sm font-semibold">
          {day ? formatDay(`${day}T12:00:00`, locale) : copy('booking.choose_time')}
        </legend>
        {timeGrid(true)}
      </fieldset>
    </div>
  ) : (
    <div className="grid gap-6">
      {section.date_picker === 'strip' ? (
        <DayStrip
          section={section}
          days={days}
          day={day}
          page={page}
          locale={locale}
          onChoose={chooseDay}
          onPage={setPage}
          legend={null}
        />
      ) : (
        <MonthCalendar
          // A new person is a new week: start on the month their diary opens.
          key={`${person ?? 'any'}-${service?.id ?? ''}`}
          section={section}
          week={week}
          day={day}
          locale={locale}
          onChoose={chooseDay}
        />
      )}
      <fieldset>
        <legend className="text-sm font-semibold">
          {day ? formatDay(`${day}T12:00:00`, locale) : copy('booking.choose_time')}
        </legend>
        {timeGrid()}
      </fieldset>
    </div>
  );

  const set = (field: keyof typeof values) => (event: { target: { value: string } }) =>
    setValues((currentValues) => ({ ...currentValues, [field]: event.target.value }));

  const detailsStep = (
    <div className={cn('grid gap-x-5 gap-y-5', !compact && 'sm:grid-cols-2')} data-booking-fields>
      <LabelledField
        label={copy('section.field_name')}
        required
        autoComplete="name"
        value={values.name}
        onChange={set('name')}
      />
      <LabelledField
        label={copy('account.phone')}
        type="tel"
        required
        autoComplete="tel"
        value={values.phone}
        onChange={set('phone')}
      />
      <LabelledField
        label={copy('checkout.field_email')}
        type="email"
        autoComplete="email"
        className={compact ? undefined : 'sm:col-span-2'}
        value={values.email}
        onChange={set('email')}
      />
      {offersWhatsApp ? (
        <WhatsAppOptIn
          className={compact ? undefined : 'sm:col-span-2'}
          checked={whatsappOptIn}
          disabled={!values.phone.trim()}
          onChange={setWhatsAppOptIn}
        />
      ) : null}
      {needsAddress ? (
        <LabelledField
          label={copy('booking.address_field')}
          multiline
          required
          autoComplete="street-address"
          className={compact ? undefined : 'sm:col-span-2'}
          value={values.address}
          onChange={set('address')}
        />
      ) : null}
      {section.ask_notes ? (
        <LabelledField
          label={section.notes_label}
          multiline
          className={compact ? undefined : 'sm:col-span-2'}
          value={values.notes}
          onChange={set('notes')}
        />
      ) : null}
      {questions.map((question) => (
        <div key={question.id} className={compact ? undefined : 'sm:col-span-2'}>
          <QuestionField
            question={question}
            value={answers[question.id]}
            onChange={(value) => setAnswers((currentAnswers) => ({ ...currentAnswers, [question.id]: value }))}
            variant="labelled"
          />
        </div>
      ))}
    </div>
  );

  // The review: every answer, each with a way back to the step that asked it
  // - when that step is one the shopper actually saw.
  const shown = new Set(steps.map((entry) => entry.kind));
  const detailRows: SummaryRow[] = [
    { key: 'name', label: copy('section.field_name'), value: values.name.trim() || null },
    { key: 'phone', label: copy('account.phone'), value: values.phone.trim() || null },
    ...(values.email.trim()
      ? [{ key: 'email', label: copy('checkout.field_email'), value: values.email.trim() }]
      : []),
    ...(offersWhatsApp && values.phone.trim()
      ? [
          {
            key: 'whatsapp',
            label: copy('booking.whatsapp_summary'),
            value: copy(optedIn ? 'booking.answer_yes' : 'booking.answer_no'),
          },
        ]
      : []),
    ...(needsAddress
      ? [{ key: 'address', label: copy('booking.address_field'), value: values.address.trim() || null }]
      : []),
    ...(section.ask_notes && values.notes.trim()
      ? [{ key: 'notes', label: section.notes_label, value: values.notes.trim() }]
      : []),
    ...questions
      .filter((question) => question.kind === 'checkbox' || String(answers[question.id] ?? '').trim())
      .map((question) => ({
        key: `q-${question.id}`,
        label: question.label.trim(),
        value:
          question.kind === 'checkbox'
            ? copy(answers[question.id] === true ? 'booking.answer_yes' : 'booking.answer_no')
            : String(answers[question.id]).trim(),
      })),
  ];
  const editLink = (kind: BookingStepKind, what: string) =>
    shown.has(kind) ? (
      <button
        type="button"
        onClick={() => go(kind)}
        aria-label={`${copy('booking.edit')} · ${what}`}
        className="shrink-0 text-sm font-semibold underline-offset-4 hover:underline"
        style={{ color: 'var(--storefront-primary)' }}
        data-review-edit={kind}
      >
        {copy('booking.edit')}
      </button>
    ) : null;
  const reviewRow = (row: SummaryRow, edit: ReactNode = null) => (
    <div key={row.key} className="flex items-start gap-3" data-review-row={row.key}>
      <dt className="w-28 shrink-0 pt-px text-xs font-medium uppercase tracking-wide" style={MUTED}>
        {row.label}
      </dt>
      <dd className="min-w-0 flex-1 break-words">
        {row.value ?? <span style={MUTED}>{copy('booking.not_chosen')}</span>}
      </dd>
      {edit}
    </div>
  );
  const groupStyle: CSSProperties = {
    borderColor: 'var(--storefront-surface)',
    borderRadius: 'var(--storefront-radius)',
  };

  const reviewStep = (
    <div className="grid gap-4">
      {/* The appointment: each line goes back to the step that chose it. */}
      <dl className="grid gap-3 border p-4 text-sm sm:p-5" style={groupStyle} data-review-group="appointment">
        {summaryRows.map((row) => reviewRow(row, row.step ? editLink(row.step, row.label) : null))}
      </dl>
      {/* Who is booking: one way back to the form that asked it all. */}
      <div className="border p-4 sm:p-5" style={groupStyle} data-review-group="details">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold">{stepLabel('details')}</p>
          {editLink('details', stepLabel('details'))}
        </div>
        <dl className="grid gap-3 text-sm">{detailRows.map((row) => reviewRow(row))}</dl>
      </div>
    </div>
  );

  const bodies: Record<BookingStepKind, ReactNode> = {
    service: serviceStep,
    person: personStep,
    datetime: datetimeStep,
    details: detailsStep,
    review: reviewStep,
  };

  // --- The frame: progress, the step, the summary --------------------------
  const total = steps.length;
  const progress = section.show_progress ? (
    <nav aria-label={copy('booking.steps_label')} className="mb-8" data-booking-progress>
      {compact ? null : (
        <ol className="hidden items-center gap-3 lg:flex" data-progress-steps>
          {steps.map((entry, position) => {
            const done = position < index;
            const here = position === index;
            const circle = (
              <span
                aria-hidden
                className="grid size-8 shrink-0 place-items-center rounded-full border text-xs font-semibold"
                style={{
                  borderColor: done || here ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
                  backgroundColor: done ? 'var(--storefront-primary)' : 'transparent',
                  color: done
                    ? 'var(--storefront-background)'
                    : here
                      ? 'var(--storefront-primary)'
                      : 'var(--storefront-muted)',
                }}
              >
                {done ? '✓' : position + 1}
              </span>
            );
            const name = (
              <span
                className={cn('text-sm', here && 'font-semibold')}
                style={{ color: here || done ? 'var(--storefront-text)' : 'var(--storefront-muted)' }}
              >
                {stepLabel(entry.kind)}
              </span>
            );
            return (
              <li
                key={entry.id}
                aria-current={here ? 'step' : undefined}
                className="flex min-w-0 flex-1 items-center gap-3 last:flex-none"
              >
                {done ? (
                  <button type="button" onClick={() => go(entry.kind)} className="flex items-center gap-2">
                    {circle}
                    {name}
                  </button>
                ) : (
                  <span className="flex items-center gap-2">
                    {circle}
                    {name}
                  </span>
                )}
                {position < total - 1 ? (
                  <span
                    aria-hidden
                    className="h-px min-w-4 flex-1"
                    style={{ backgroundColor: done ? 'var(--storefront-primary)' : 'var(--storefront-surface)' }}
                  />
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
      <div className={compact ? undefined : 'lg:hidden'} data-progress-bar>
        <div className="flex items-baseline justify-between gap-3 text-xs">
          <span style={MUTED}>{copy('booking.step_of', { n: index + 1, total })}</span>
          <span className="font-semibold">{stepLabel(step.kind)}</span>
        </div>
        <div
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={index + 1}
          aria-valuetext={copy('booking.step_of', { n: index + 1, total })}
          className="mt-2 h-1 w-full overflow-hidden rounded-full"
          style={{ backgroundColor: 'var(--storefront-surface)' }}
        >
          <div
            className="h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none"
            style={{ width: `${((index + 1) / total) * 100}%`, backgroundColor: 'var(--storefront-primary)' }}
          />
        </div>
      </div>
    </nav>
  ) : null;

  const summaryCard = (
    <div className="border p-5" style={CARD_STYLE}>
      <p className="mb-4 text-sm font-semibold" style={STOREFRONT_HEADING_STYLE}>
        {copy('booking.summary_title')}
      </p>
      <SummaryList rows={summaryRows} />
    </div>
  );

  const side = section.show_summary && !compact && section.summary_position === 'side';
  const barId = `${section.id}-summary`;
  // On a phone (and in the narrow card), the summary folds into a bar above
  // the buttons: one line of what is chosen so far, opening to the full list.
  const bar = section.show_summary ? (
    <div
      className={cn('mt-6 border', !compact && 'lg:hidden')}
      style={{ borderColor: LINE, borderRadius: 'var(--storefront-radius)' }}
      data-booking-summary="bar"
    >
      <button
        type="button"
        aria-expanded={summaryOpen}
        aria-controls={barId}
        onClick={() => setSummaryOpen(!summaryOpen)}
        className="flex w-full items-center gap-3 px-4 py-3 text-start"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{copy('booking.summary_title')}</span>
          <span className="block truncate text-xs" style={MUTED}>
            {[
              service?.name,
              chosen
                ? formatTime(chosen, locale, zone)
                : token
                  ? copy('booking.your_token', { number: token.next_token })
                  : null,
            ]
              .filter(Boolean)
              .join(' · ') ||
              copy('booking.not_chosen')}
          </span>
        </span>
        <span
          aria-hidden
          className={cn('text-base transition-transform motion-reduce:transition-none', summaryOpen && 'rotate-180')}
        >
          ⌄
        </span>
      </button>
      {summaryOpen ? (
        <div id={barId} className="border-t px-4 py-4" style={{ borderColor: LINE }}>
          <SummaryList rows={summaryRows} />
        </div>
      ) : null}
    </div>
  ) : null;

  const stepForm = (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        advance();
      }}
      data-booking-step={step.kind}
      className={compact ? undefined : 'border p-5 sm:p-7'}
      style={compact ? undefined : CARD_STYLE}
    >
      <h3
        ref={heading}
        tabIndex={-1}
        className="text-lg font-semibold outline-none sm:text-xl"
        style={STOREFRONT_HEADING_STYLE}
      >
        {stepLabel(step.kind)}
      </h3>
      <div key={step.kind} className="mt-5" data-booking-step-body>
        {bodies[step.kind]}
      </div>

      {bar}

      {isLast ? <BookingPaymentOptions payment={payment} className="mt-5" /> : null}

      {state === 'error' ? (
        <p className="mt-4 text-sm" role="alert" style={{ color: 'var(--storefront-text)' }}>
          {error || copy('booking.error')}
        </p>
      ) : null}

      <div
        className={cn('mt-6 flex items-center gap-3', !compact && 'border-t pt-5 sm:pt-6')}
        style={{ borderColor: LINE }}
      >
        {index > 0 ? (
          <button
            type="button"
            onClick={() => go(steps[index - 1].kind)}
            className="h-12 border px-6 text-sm font-semibold transition-colors hover:opacity-80"
            style={{ borderColor: LINE, borderRadius: 'var(--storefront-radius)' }}
          >
            {copy('booking.back')}
          </button>
        ) : null}
        <button
          type="submit"
          disabled={
            state === 'sending' || (!preview && !answered(step.kind)) || (isLast && paused !== null)
          }
          className={cn(
            'ms-auto h-12 px-8 text-sm font-semibold disabled:opacity-50',
            compact && index === 0 && 'w-full',
          )}
          style={STOREFRONT_BUTTON_STYLE}
          data-booking-next
        >
          <span className="inline-flex items-center justify-center gap-2">
            {state === 'sending' ? copy('account.sending') : isLast ? section.button_label : copy('booking.next')}
            {state !== 'sending' && !isLast ? (
              <svg aria-hidden viewBox="0 0 16 16" className="size-4" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : null}
          </span>
        </button>
      </div>
      {isLast ? <CheckoutPausedNote message={paused} className="mt-3 text-sm" /> : null}
    </form>
  );

  return (
    <div
      className={compact ? 'mt-4' : 'mt-8'}
      data-booking-wizard
      data-booking-compact={compact || undefined}
    >
      <style>{MOTION_CSS}</style>
      {progress}
      <div className={cn(side && 'lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-10')}>
        <div className="min-w-0">{stepForm}</div>
        {side ? (
          <aside className="hidden lg:sticky lg:top-24 lg:block" data-booking-summary="side">
            {summaryCard}
          </aside>
        ) : null}
      </div>
      {section.show_summary && !side && !compact ? (
        <div className="mt-8 hidden max-w-xl lg:block" data-booking-summary="bottom">
          {summaryCard}
        </div>
      ) : null}
    </div>
  );
}
